import { useState, useEffect } from "react";
import { BookingSupplierCostServices } from "../services/booking-supplier-cost";
import { ExpectingSupplierPaymentServices } from "../services/expect-supplier-payment";
import { SupplierServices } from "../services/supplier";
import { MasterDataServices } from "../services/master-data";
import { ISuppliersByNameResponse, ISuppliersByNameResult } from "../interfaces/supplier";
import { MASTERDATA_TYPE } from "../constants/master-data";
import { IMasterDataType } from "../interfaces/master-data";

type ServiceType = "bscServices" | "espServices";

interface UseDropdownsDataParams {
  serviceType: ServiceType;
  enabled?: boolean;
}

interface DropdownsDataResult {
  suppliers?: ISuppliersByNameResult[];
  statuses?: string[];
  bookingRefs?: string[];
  leadPaxNames?: string[];
  currencies?: IMasterDataType[];
  parentBookingRefs?: string[];
}

const typeToKeyMap: Record<string, keyof DropdownsDataResult> = {
  statuses: "statuses",
  bookingRefs: "bookingRefs",
  leadPaxNames: "leadPaxNames",
  parentBookingRefs: "parentBookingRefs",
};

async function fetchDropdownByType(
  serviceType: ServiceType,
  type: string
): Promise<{ key: keyof DropdownsDataResult; data: any[] }> {
  const res =
    serviceType === "bscServices"
      ? await BookingSupplierCostServices.getDropdownByType(type)
      : await ExpectingSupplierPaymentServices.getDropdownByType(type);

  return {
    key: typeToKeyMap[type],
    data: res?.data || [],
  };
}

async function fetchCurrencies(): Promise<{ key: keyof DropdownsDataResult; data: any[] }> {
  const res = await MasterDataServices.getAllByType(MASTERDATA_TYPE.CURRENCY);
  return { key: "currencies", data: res?.data || [] };
}

async function fetchSuppliers(): Promise<{ key: keyof DropdownsDataResult; data: any[] }> {
  const res: ISuppliersByNameResponse = await SupplierServices.getSuppliersByName({
    params: { page: 1, pageSize: 5000 },
  });
  return { key: "suppliers", data: res?.data?.results || [] };
}

export const useDropdownsData = ({ serviceType, enabled = true }: UseDropdownsDataParams) => {
  const [data, setData] = useState<DropdownsDataResult>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [reloadCounter, setReloadCounter] = useState(0);

  const reload = () => setReloadCounter((prev) => prev + 1);

  useEffect(() => {
    if (!enabled) return;
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const types = ["statuses"];

        const promises: Promise<{ key: keyof DropdownsDataResult; data: any[] }>[] = [
          ...types.map((type) => fetchDropdownByType(serviceType, type)),
          fetchCurrencies(),
          fetchSuppliers(),
        ];

        const results = await Promise.all(promises);

        const resultObj = results.reduce((acc, curr) => {
          acc[curr.key] = curr.data;
          return acc;
        }, {} as DropdownsDataResult);

        setData(resultObj);
      } catch (err: any) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [serviceType, reloadCounter, enabled]);

  return { data, loading, error, reload };
};
